import { useMemo, useState, useContext } from "react";
import binocularIcon from "images/binocular.svg";
import { useResumeUploadFlow } from "hooks/useResumeUploadFlow";
import ResumePreviewModal from "components/ResumePreviewModal";
import FileCloudinaryWidget from "components/CloudinaryWidget/FileCloudinaryWidget";
import ResumeWarningModal from "components/UserCardList/components/UserCardResult/molecules/Resume/ResumeWarningModal";
import { getUserName } from "utils/helpers/users";
import { MatchContext, SearchContext } from "context/providers";

const ListViewResumeButton = ({ hit }) => {
  const { updateMatchUserFieldLocally, matches } = useContext(MatchContext);
  const { updateHit } = useContext(SearchContext);

  const [resumeDetails, setResumeDetails] = useState({
    resumeFileName: `${hit.resumeLocation?.split("/")[2]}.pdf`,
    resumeLocation: hit.resumeLocation,
  });

  const saveAttributes = async (attributes) => {
    const attributesToUpdate = {
      input: {
        id: hit.id,
        ...attributes,
      },
    };
    try {
      await updateHit(attributesToUpdate);

      if (matches.length > 0) {
        const applicationId =
          hit.match?.applicationId ||
          matches.find(({ userId }) => userId === hit.id)?.applicationId;

        if (applicationId) {
          updateMatchUserFieldLocally(
            applicationId,
            "resumeLocation",
            attributes.resumeLocation
          );
        }
      }
    } catch (error) {
      console.log("Error when updating profile info", error);
    }
  };

  const handleResumeUpload = async (resumeStorageLocation) => {
    await saveAttributes({ resumeLocation: resumeStorageLocation });
    setResumeDetails({
      resumeFileName: `${resumeStorageLocation.split("/")[2]}.pdf`,
      resumeLocation: resumeStorageLocation,
    });
  };

  const {
    isPreviewModalOpen,
    isUploadWidgetOpen,
    isWarningModalOpen,
    setIsPreviewModalOpen,
    handleInitiateUpload,
    handleUploadComplete,
    handleClosePreview,
    handleCloseWarning,
    handleOpenWarningWidget,
    handleCloseUploadWidget,
  } = useResumeUploadFlow({
    onUploadSuccess: handleResumeUpload,
  });

  const previewUser = useMemo(
    () => ({
      ...hit,
      resumeLocation: resumeDetails?.resumeLocation,
    }),
    [hit, resumeDetails?.resumeLocation]
  );

  return (
    <>
      <button
        type="button"
        className="flex items-center gap-1 px-1 py-1.5 text-sm text-gray-900 hover:text-black hover:bg-gray-100 rounded transition-colors shrink-0"
        title="Resume"
        onClick={(e) => {
          e.stopPropagation();
          setIsPreviewModalOpen(true);
        }}
      >
        <img
          src={binocularIcon}
          alt=""
          className="w-[18px] h-[18px] grayscale opacity-80 shrink-0"
        />
      </button>

      <ResumePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreview}
        currentUser={previewUser}
        onInitiateUpload={handleInitiateUpload}
      />

      {isUploadWidgetOpen && (
        <FileCloudinaryWidget
          onUpload={handleUploadComplete}
          identifier="resume-upload-list-view"
          minImageHeight={517}
          minImageWidth={1839}
          resourceType="aspose"
          fileName={`${getUserName(hit)}_resume`}
          icon="upload"
          tooltip="upload resume"
          openWidget={isUploadWidgetOpen}
          setLoadingWidget={handleCloseUploadWidget}
        />
      )}

      {isWarningModalOpen && (
        <ResumeWarningModal
          loadingWidget={isUploadWidgetOpen}
          onClose={handleCloseWarning}
          onUploadClickButton={handleOpenWarningWidget}
        />
      )}
    </>
  );
};

export default ListViewResumeButton;
